// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductGridContainer, { ProductGridContainerStyle } from "@sc/App/Components/Product/ProductGridTemplate";
import ProductPriceAndActions, { ProductPriceAndActionsStyle } from "@sc/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { ProductListProductInformationStyles } from "@sc/Overrides/Widgets/ProductList/ProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";

// <XNG-Changes>
const mapStateToProps = (state: CustomAppState) => {
    return {
        isTablet: state.xCommon.isTabletScreen,
    };
};
// </XNG-Changes>

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productListImageStyles?: ProductListProductImageStyles;
    productInformationStyles?: ProductListProductInformationStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    isTablet,
}) => {
    return (
        <ProductGridContainer
            extendedStyles={styles.productGridContainerStyles}
            id={`product_${product.id}`}
            data-test-selector="productContainer"
        >
            <ProductGridContainer.Image>
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={false}
                    showImageBadge={false}
                    showTextBadge={showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListImageStyles}
                />
            </ProductGridContainer.Image>
            <ProductGridContainer.Info>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={false}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={false}
                    maxTextBadges={maximumNumberOfTextBadges}
                    {...{ extendedStyles: styles.productInformationStyles }}
                />
            </ProductGridContainer.Info>
            <ProductGridContainer.PriceAndActions>
                <ProductPriceAndActions
                    showAvailability={showAvailability}
                    showCompare={showCompare}
                    showAddToList={showAddToList}
                    showPrice={showPrice}
                    showImageBadges={showImageBadges}
                    showTextBadges={showTextBadges}
                    maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                    maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                    showAddToCartActions={true}
                    extendedStyles={styles.productPriceAndActionsStyles}
                />
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProduct(ProductListProductGridCard);
